import { BlueprintMinimalInfo } from "./Blueprints";
import { NameType } from "./Person";
import { WORKFLOW_STATUS_ENUM, WORKFLOW_STEP_STATUS_ENUM } from "./Workflows";

export interface ReportInfoCompany {
  id: number;
  external_id: string;
  created_at: string;
  modified_at: string;
  is_deleted: boolean;
  modified_by: string;
  name: string;
  notes: string;
  is_active: boolean;
}

export interface ReportSummaryItemApi {
  has_rerun: boolean;
  report_uuid: string;
  report_date_added: string;
  report_status: string;
  client_report_id: string;
  total_ratings: number;
  full_name: string;
  workflow_run_uuid: string;
  workflow_status: string;
  flags_count_payload?: FlagsCountPayload;
  finish_at: string;
  created_at: string;
  due_date: string;
  due_date_updated: boolean;
  is_locked: boolean;
  sla_met?: boolean;
  step_name: string | null;
  company_name: string;
  company_uuid: string;
  company_note: string;
  banner_notes?: string;
  parent_company_name: string;
  parent_company_uuid: string;
  locked_by: string;
  locked_by_name: string;
  requested_by?: string;
  requested_by_name?: string;
  person_uuid: string;
  blueprint_uuid: string;
  blueprint_name: string;
  report_type?: string;
  /** When true, navigate completed/reviewed rows to /360/:reportId/summary. */
  is_360?: boolean;
  behavior_kit_uuid?: string;
  behavior_kit_name?: string;
  recur?: string;
  step_payload?: any;
  was_amend: boolean;
  was_qa?: boolean;
  job_title: string;
}

export interface FlagsCountPayload {
  total?: number;
  posts_count?: number;
  community_count?: number;
  articles_count?: number;
  adverse_flag_count?: number;
  keywords?: number;
  news?: number;
  legal?: number;
  diligence?: number;
  topics?: number;
}

export interface ComputeFlagsCountTotalOptions {
  misconduct_only?: boolean;
}

/**
 * Sum of insight items.
 * @param payload - flags_count payload
 * @param options - misconduct_only: if true, sum only community + adverse + articles + keywords; if false (default), sum all parts (360 style: + news + legal + diligence).
 */
export function computeFlagsCountTotal(
  payload: FlagsCountPayload | undefined,
  options: ComputeFlagsCountTotalOptions = {}
): number {
  const { misconduct_only = false } = options;
  if (!payload) return 0;
  const base =
    (payload.community_count ?? 0) +
    (payload.adverse_flag_count ?? 0) +
    (payload.articles_count ?? 0) +
    (payload.keywords ?? 0) +
    (payload.topics ?? 0);
  if (misconduct_only) return base;
  return (
    base + (payload.news ?? 0) + (payload.legal ?? 0) + (payload.diligence ?? 0)
  );
}

export interface ReportSummaryApi {
  report_summaries: ReportSummaryItemApi[];
  message: string;
  total_rows: number;
  timestamp?: number;
}
export interface ReportRerunSummary {
  report_uuid: string;
  parent_report_uuid: string;
  started_at: string;
  finished_at: string;
}

export enum REPORT_STATUS_ENUM {
  NEW = "NEW",
  IN_PROGRESS = "IN_PROGRESS",
  RE_RUN = "RE_RUN",
  DONE = "DONE",
  CANCEL = "CANCELLED",
  AMENDMENT = "AMENDMENT",
  WEB_CONTENT = "WEB_CONTENT",
}

/**
 * Converts a report status enum value to a human-readable string
 * @param status - The report status enum value
 * @returns A human-readable string representation of the status
 */
export const getReportStatusDisplayName = (
  status: REPORT_STATUS_ENUM | string
): string => {
  switch (status) {
    case REPORT_STATUS_ENUM.NEW:
      return "New";
    case REPORT_STATUS_ENUM.IN_PROGRESS:
      return "In Progress";
    case REPORT_STATUS_ENUM.RE_RUN:
      return "Re-run";
    case REPORT_STATUS_ENUM.DONE:
      return "Done";
    case REPORT_STATUS_ENUM.CANCEL:
      return "Cancelled";
    case REPORT_STATUS_ENUM.AMENDMENT:
      return "Amendment";
    case REPORT_STATUS_ENUM.WEB_CONTENT:
      return "Web Content";
    default:
      return status || "Unknown";
  }
};

export enum WORKFLOW_REPORT_STATUS_ENUM {
  COMPLETED = "completed",
  IN_PROCESS = "in_process",
  IN_PROGRESS = "in_progress",
  AMENDMENT = "amendment",
  INTERNAL_QA = "internal_qa",
  CLIENT_QA = "client_qa",
}

export enum REPORT_TYPES {
  MISCONDUCT = "MISCONDUCT",
  INSTANTFIT = "INSTANTFIT",
  THREE_SIXTY = "360",
}

export const MIN_WRITING_SAMPLE_LENGTH = 400;

export interface ReportListInputApi {
  person_name?: string;
  company_name?: string;
  workflow_status?: (WORKFLOW_REPORT_STATUS_ENUM | WORKFLOW_STATUS_ENUM)[];
  blueprint_uuid?: string;
  min_due_date?: string;
  max_due_date?: string;
  page_size: number;
  page?: number;
  workflow_step_name_csv?: WORKFLOW_STEP_STATUS_ENUM[];
  without_steps?: boolean;
  descending?: boolean;
  exclude_cancelled?: boolean;
  /** When 1, external interface maps to workflow failed_only (FAILED runs). UI sends only with report:resume_workflow. */
  errors_only?: number;
  canViewAllReportsWithinCompany?: boolean; // Not part of payload, used for frontend permission logic
}

export interface ReportsPendingReviewRequest {
  individual?: boolean;
  company_uuids?: string[];
  search?: string;
  page_size: number;
  page?: number;
}

export interface ReportsPendingReviewItem {
  report_uuid: string;
  full_name: string;
  company_uuid: string;
  company_name: string;
  blueprint_uuid: string;
  blueprint_name: string;
  behavior_kit_uuid?: string;
  behavior_kit_name?: string;
  created_at: Date;
  finished_at: Date;
  client_report_id: string;
  requested_by: string;
  flags_count_payload?: FlagsCountPayload;
  workflow_run_uuid: string;
  person_uuid: string;
  workflow_payload?: any;
  was_amend: boolean;
  job_title: string;
  recur?: string;
  /** When true, navigate to /360/:reportId/summary; otherwise /reports/:id/summary */
  is_360?: boolean;
}

export interface ReportsPendingReviewResponse {
  items: ReportsPendingReviewItem[];
  total_items: number;
  page: number;
  page_size: number;
  message: string;
  timestamp?: number;
}

export interface NameState {
  first_name: string;
  middle_name: string;
  last_name: string;
  name_type: NameType;
}

export interface JobState {
  title: string;
  employer: string;
  country: string;
  start_date?: string;
  end_date?: string;
}

export interface AddressesState {
  line1: string;
  line2: string;
  city: string;
  state: string;
  zip?: string;
  country: string;
  start_date?: string;
  end_date?: string;
}

export interface DegreesState {
  name: string;
  educator: string;
  country: string;
  start_date?: string;
  end_date?: string;
}

export interface PhonesState {
  raw_number: string;
}

export interface EmailState {
  address: string;
}

export type ProfilesState = string[];

export interface PersonFormState {
  birth_date: string;
  blueprint_uuid: string;
  behavior_kit_uuid: string;
  company_uuid: string;
  names: NameState[];
  jobs: JobState[];
  addresses: AddressesState[];
  degrees: DegreesState[];
  phones: PhonesState[];
  emails: EmailState[];
  profiles: string[];
  custom_id: string;
}

export interface PersonFormStateV2 {
  person_uuid: string;
  birth_date: string;
  blueprint_uuids: BlueprintMinimalInfo[];
  behavior_kit_uuid: string;
  company_uuid: string;
  names: NameState[];
  jobs: JobState[];
  addresses: AddressesState[];
  degrees: DegreesState[];
  phones: PhonesState[];
  emails: EmailState[];
  profiles: string[];
  custom_id: string;
  job_title: string;
  client_qa: boolean;
  internal_qa: boolean;
}

export interface CreateReportInputApi {
  person_info: {
    names: NameState[];
    jobs: JobState[];
    addresses: AddressesState[];
    degrees: DegreesState[];
    phones: PhonesState[];
    emails: EmailState[];
    birth_date?: string;
    modified_by?: string; // WARNING!!! TD-1: remove this property once the Authorization cicle gets completed
  };
  profile_urls: ProfilesState;
  blueprint_uuid: string;
  company_uuid: string;
  behavior_kit_uuid?: string;
  custom_id?: string;
  rerun_from_parent_uuid?: string;
}

export interface CheckTypeInputApi {
  blueprint_uuid: string;
  custom_fields: {
    custom_id: string;
    job_title: string;
    job_requisition_number?: string;
    writing_sample?: string;
    behavior_kit?: string;
  };
}
export interface CheckTypeInputApi2025 {
  check_type_uuid: string;
  behavior_kit_uuid?: string;
  custom_fields: {
    custom_id?: string;
    job_title?: string;
    job_requisition_number?: string;
    writing_sample?: string;
    assessment_id?: string;
    assignment?: string;
    reference?: string;
  };
}

export interface CreateReportInputV2Api {
  person_info: {
    external_id?: string;
    names: NameState[];
    jobs: JobState[];
    addresses: AddressesState[];
    degrees: DegreesState[];
    phones: PhonesState[];
    emails: EmailState[];
    birth_date?: string;
    modified_by?: string; // WARNING!!! TD-1: remove this property once the Authorization cicle gets completed
  };
  check_types: CheckTypeInputApi[];
  profile_urls: ProfilesState;
  company_uuid: string;
  rerun_from_parent_uuid?: string;
  client_qa: boolean;
  internal_qa: boolean;
}

export interface CreateReportInputV2025Api {
  person: {
    person_uuid?: string;
    names: NameState[];
    jobs: JobState[];
    addresses: AddressesState[];
    degrees: DegreesState[];
    phones: PhonesState[];
    emails: EmailState[];
    birth_date?: string;
    modified_by?: string; // WARNING!!! TD-1: remove this property once the Authorization cicle gets completed
  };
  check_types: CheckTypeInputApi2025[];
  profiles: ProfilesState;
  rerun_from_parent_uuid?: string;
  cqa: boolean;
  iqa: boolean;
  company_uuid: string;
  recur?: {
    schedule_type: string;
    schedule_value: string;
  };
}

export interface ReportsAdminCompletedInput {
  filter?: {
    [key: string]: string[];
  };
  page_size: number;
  page?: number;
}

export interface ReportsAdminOperationsInput {
  filter?: {
    [key: string]: string[] | string;
  };
  page_size: number;
  page?: number;
}

export interface CreateReportNoteRequest {
  report_uuid: string;
  note: string;
  actor_uuid: string;
}

export interface ReportLog {
  external_id: string;
  log_message: string;
  type: string;
  created_at: Date;
  modified_at: Date;
  modified_by: string;
  name: string;
  is_user: boolean;
}

export interface ReportBaseApi {
  external_id: string;
  created_at: Date;
  modified_at: Date;

  modified_by: string;
  started_at: Date;
  person_uuid: string;
  company_uuid: string;
  blueprint_uuid: string;
  workflow_run_uuid: string;
  custom_id: string;
  status: string;
  finished_at: Date;
}
export interface SourceTypeFilter {
  facebook: boolean;
  twitter: boolean;
  instagram: boolean;
  web: boolean;
}

export interface PostTypeFilter {
  original: boolean;
  shared: boolean;
  liked: boolean;
}

export interface MappedCSVReport {
  Name: string;
  Company: string;
  CommunityFlagCount: number;
  AdverseFlagCount: number;
  KeywordCount: number;
  ArticleCount: number;
  DateAdded: string;
  DateCompleted: string;
  ReportURL: string;
}

export interface LegacyReportResponse {
  reports_returned: number;
  reports_total: number;
  next_token: string;
  reports: LegacyReportSummaryItem[];
  timestamp: number;
}

export interface LegacySearchResponse {
  reports_returned: number;
  reports_total: number;
  has_more: boolean;
  reports: LegacyReportSummaryItem[];
}

export interface LegacyReportSummaryItem {
  uuid: string;
  reportId: string;
  personId: string;
  flagKitVersionId: string;
  companyId: string;

  name: string;
  firstName: string;
  middleName: string;
  lastName: string;
  dateOfBirth: number;
  role: string;

  emailList: string[];
  aliasList: string[];
  phoneList: string[];
  locationList: string[];
  degreeList: string[];
  jobList: string[];

  dateModified: number; // date-time
  datesFinished: number[]; // date-time
  datesProcessing: number[]; // date-time
  personStatus: string;
  reviewStatus: string;
  updateType: string;
  numberOfReports: number;

  creator: string;
  screeningCompanyName: string;
  date: number;
  flagCount: number;
  flagTypes: string[];
  mediaEntries: string[];
  searchId: string;
  searchName: string;
  searchUpToDate: boolean;

  candidateStatus: string;
  contactEmail: string;
  investigator: boolean;
  lookbackWindow: number;
  paused: boolean;
  percentAccepted: number;
  permission: string;
  permissions: string;
  processingMetrics: string;
  profiles: string[];
  priority: string;
  visiblePriority: string;
  contractId: string;
  webPersonMetrics: LegacyWebPersonMetrics;
}
export interface LegacyWebPersonMetrics {
  totalBadFlagCount: number;
  totalGoodFlagCount: number;
  keywordNewsFlagCount: number;
  keywordPostFlagCount: number;
  relevantNewsFlagCount: number;
}

export interface LegacyReportRequest {
  company_uuid: string;
  next_token?: string;
  search?: string;
  limit?: number;
}

export interface CreateImageRequest {
  report_uuid: string;
  content: string;
  file_name: string;
}

export interface GetImagePresignedUrlRequest {
  report_uuid: string;
  file_name: string;
}

export interface ReportsByStepNameParams {
  workflow_step_names: string;
  page: string;
  page_size: string;
  min_due_date?: string;
  max_due_date?: string;
  days?: string;
}

export interface ProductReport {
  external_id?: string;
  status: string;
  report_type: REPORT_TYPES;
  blueprint_uuid?: string;
  reviewed: boolean;
  content: any;
  name: string;
  due_date?: string;
  finished_at?: string;
}

export interface MarkReviewedInput {
  product_reports_uuids: string[];
}

export interface CreateActorTimerRequest {
  name: string;
  value: number;
  workflow_run_uuid: string;
  finished_at: string;
  status: ACTOR_TIMER_STATUS_ENUM | ACTOR_TIMER_STATUS_UI_ENUM;
}

export enum ACTOR_TIMER_STATUS_ENUM {
  OPEN = "OPEN",
  CLOSED = "CLOSED",
  ABANDONED = "ABANDONED",
}

export enum ACTOR_TIMER_STATUS_UI_ENUM {
  OPEN = "OPEN_UI",
  CLOSED = "CLOSED_UI",
  ABANDONED = "ABANDONED_UI",
}
